import { FaArrowLeft, FaSearch, FaTrash } from "react-icons/fa";
import { useNavigate, useParams, Navigate, useSearchParams, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { useModal } from "../../context/ConfirmModalContext";
import { attachCurrentTime, dateDuration, formatDateForInput, getBookingBusinessDate } from "../../utils/date";
import type Room from "../../models/Room";
import { initAllStores } from "../../utils/cache";
import { v4 as uuidv4 } from "uuid";
import FullPageSpinner from "../../components/FullPageSpinner";
import { toBool } from "../../utils/helper-function";
import { autoFillBookingForm, checkRoomAvailability, getBookingReceiptSummary } from "../../utils/booking-helper";
import { sendRequest, } from "../../utils/api";
import { calculateBillCharge, generatedBillNo, generateReference, numberToWords, splitMoney } from "../../utils/bill";
import BookedRoom from "../../models/BookedRoom";
import { BillData } from "../../types/bill";
import Bill from "../../Modal/accounting/BillModal";
import Hotel from "../../models/Hotel";
import { syncServices } from "../../utils/sync";
import ReceiptModal from "../../Modal/accounting/ReceiptModal";
import { ReceiptData } from "../../types/print-setting";
import { capitalizeFirst } from "../../utils/string";
import { EARLY_CHECKIN_AMOUNT, SHORT_TIME_AMOUNT, SHORT_TIME_DURATION } from "../../constants";
import { terminal } from "../../utils/user-helper";

// ---------------------- CONSTANTS ----------------------
const today = formatDateForInput(new Date());

// ---------------------- COMPONENT ----------------------
export default function BookRoom() {
    const navigate = useNavigate();
    const { action } = useParams<{ action: BookingAction }>();
    const [searchParams] = useSearchParams();
    const location = useLocation();


    const [type, setType] = useState("text");

    const { openModal, closeModal } = useModal();

    // ---------------------- ACTION DEFINITIONS ----------------------
    const actions: {
        book: { title: string; url: string };
        reserve: { title: string; url: string };
        // extend: { title: string; url: string };()
    } = {
        book: { title: "Booking", url: "" },
        reserve: { title: "Reservation", url: "" },
        // extend: { title: "Extension", url: "" },
    };

    const [terminalId] = useState(() => terminal());

    // ---------------------- STATES ----------------------
    const [Allrooms, setAllRooms] = useState<Room[]>([]);
    const [selectedRooms, setSelectedRooms] = useState<Room[]>([]);

    const [guestSearch, setGuestSearch] = useState("");
    const [searchResults, setSearchResults] = useState<any>([])
    const [showSearchResults, setShowSearchResults] = useState(false);

    const [bill, setBill] = useState<BillData>({} as BillData);
    const [showBill, setShowBill] = useState(false);

    const [showReceiptModal, setShowReceiptModal] = useState(false);
    const [receiptData, setReceiptData] = useState<ReceiptData>({} as ReceiptData);
    // Guest Details
    const [customerId, setCustomerId] = useState<string>("");
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [phone, setPhone] = useState("");
    const [address, setAddress] = useState("");
    const [state, setState] = useState("");
    const [nationality, setNationality] = useState("");
    const [gender, setGender] = useState("");
    const [idType, setIdType] = useState("");
    const [idNumber, setIdNumber] = useState("");
    const [vechicleNumber, setVechicleNumber] = useState("");
    const [occupation, setOccupation] = useState("");

    // Booking Details
    const [checkInDate, setCheckInDate] = useState(today);
    const [checkOutDate, setCheckOutDate] = useState("");
    const [discount, setDiscount] = useState<string>("");
    const [guestNumber, setGuestNumber] = useState("");
    const [paymentMethod, setPaymentMethod] = useState("");
    const [description, setDescription] = useState("");
    const [isPaid, setIsPaid] = useState("");
    const [isDisabled, setDisabled] = useState<boolean>(false);
    const [displayDuration, setDisplayDuration] = useState("");
    const [earlyCheckIn, setEarlyCheckin] = useState<BoolStr>("" as BoolStr);
    const [duration, setDuration] = useState<number>(0);
    const [retrieveId] = useState<{
        roomId?: string | null, bookingId?: string | null, customerId?: string | null,
    }>({
        roomId: searchParams.get("roomId"),
        bookingId: searchParams.get("bookingId"),
        customerId: searchParams.get("customerId"),
    });
    const [bookingType, setBookingType] = useState(
        action && actions[action]["title"].toLocaleLowerCase() === "booking"
            ? "fullTime" : "reservation"
    );

    // Totals
    const [roomTotal, setRoomTotal] = useState(0);
    const [finalTotal, setFinalTotal] = useState(0);

    const [user, setUser] = useState({} as any);
    const [hotel, setHotel] = useState<Hotel>({} as Hotel);

    // Loading
    const [loading, setLoading] = useState(false);

    // ---------------------- EFFECT: LOAD ROOMS ----------------------
    useEffect(() => {
        const init = async () => {
            const cache = await initAllStores();
            const cachedRooms: Room[] = await cache.getAllBy("rooms", { terminal: terminalId }, "number");
            setAllRooms(cachedRooms);
            setSelectedRooms(cachedRooms.filter(r => r.id === retrieveId.roomId));

            const user = await cache.getBy("data", { id: "user" });
            const hotel: Hotel | null = await cache.getBy("data", { id: "hotel" });

            if (!user || !hotel) {
                return openModal(
                    "Session Expired ⚠️",
                    "We couldn’t find your user information. Please log in again to continue.",
                    () => {
                        closeModal();
                        window.location.href = "/login";
                    },
                    false,
                    "Login Again"
                );
            }

            setUser(user);
            setHotel(hotel);

            if (action === "book") setDisabled(true);
        };
        init();
    }, []);

    // ---------------------- UTILITIES ----------------------
    const recalcDuration = (checkIn: string, checkOut: string) => {
        if (!checkIn || !checkOut) {
            setDuration(0);
            setDisplayDuration("");
            return;
        }
        const diff = dateDuration(checkIn, checkOut);
        setDuration(diff);
        if (bookingType.toLocaleLowerCase() === "shorttime") {
            setDisplayDuration(
                `${hotel?.preference?.shortTimeDuration
                ?? SHORT_TIME_DURATION
                } Hrs (Short Time)`
            );
        } else {
            setDisplayDuration(`${diff} Night (Full Time)`);
        }
    };

    const updateCheckInDate = (checkin: string) => {
        // if (new Date(checkin) < new Date(today)) {
        //     return openModal("Invalid Date", "Check-in date cannot be in the past.", closeModal, false, "Close");
        // }
        setCheckInDate(checkin);
        recalcDuration(checkin, checkOutDate);
    };

    const updateCheckOutDate = (checkout: string) => {
        if (!checkInDate) {
            return openModal("Missing Date", "Please select a check-in date first.", closeModal, false, "Close");
        }
        if (new Date(checkout) < new Date(checkInDate)) {
            return openModal("Invalid Date", "Checkout date must be after the check-in date.", closeModal, false, "Close");
        }
        setCheckOutDate(checkout);
        recalcDuration(checkInDate, checkout);
    };

    // ---------------------- EFFECT: AUTO FILL FORM ----------------------
    useEffect(() => {
        const run = async () => {

            try {
                if (retrieveId.bookingId) {

                    const customerId = await autoFillBookingForm(retrieveId.bookingId, {
                        setName,
                        setEmail,
                        setPhone,
                        setAddress,
                        setState,
                        setNationality,
                        setGender,
                        setIdType,
                        setIdNumber,
                        setOccupation,
                        setDescription,
                        setDiscount,
                        setGuestNumber,
                        setVechicleNumber
                    });

                    setCustomerId(customerId);
                } else if (retrieveId.customerId) {
                    // const { state }
                    const guest = location.state;
                    fillGuestDetails(guest);
                }
            } catch (err: any) {
                console.log(err);
            }
        };
        run();

    }, [action, retrieveId.bookingId, retrieveId.customerId]);

    // ---------------------- ROOM HANDLING ----------------------
    const rooms: { book: Room[], reserve: Room[] } = {
        "book": Allrooms.filter(room => !room.isOccupied),
        "reserve": Allrooms,
    };

    const handleAddRoom = (roomId: string) => {
        const room = rooms[action!].find((r) => r.id === roomId);
        if (room && !selectedRooms.some((r) => r.id === roomId)) {
            setSelectedRooms([...selectedRooms, room]);
        }
    };

    const handleRemoveRoom = (roomId: string) => {
        setSelectedRooms(selectedRooms.filter((r) => r.id !== roomId));
    };

    // ---------------------- SHOW CONFIRMATION MODAL ----------------------
    const showConfirmationModal = () => {
        const missingFields: string[] = [];
        if (!name) missingFields.push("Guest Name");
        if (!address) missingFields.push("Guest Address");
        if (!phone) missingFields.push("Guest Phone Number");
        if (!checkInDate) missingFields.push("Checkin Date");
        if (!checkOutDate) missingFields.push("Checkout Date");
        if (!paymentMethod) missingFields.push("Payment Method");
        if (!isPaid) missingFields.push("Payment Status");
         if (!earlyCheckIn) missingFields.push("Early Checkin");
        if (selectedRooms.length === 0) missingFields.push("Rooms");

        // console.log(attachCurrentTime(checkInDate))

        for (const room of selectedRooms) {
            if (room.isOccupied && action !== "reserve") {
                return openModal(
                    "Room Occupied ⚠️",
                    `Room ${room.number} is currently occupied and cannot be booked.`,
                    () => { }, // No callback needed for just info
                    false, "close"
                )
            }
        }

        if (missingFields.length > 0) {
            return openModal(
                "Missing Fields",
                `Please fill in: ${missingFields.join(", ")}`,
                closeModal, false, "Close"
            );
        }

        openModal(`Confirm ${action && actions[action]["title"]}`,
            `Are you sure you want to continue with this ${action && actions[action]["title"].toLowerCase()}?`,
            () => {
                if (!action) return;
                // else if (action === "extend") {
                //     BookingAction[action](
                //         retrieveRoomId, checkOutDate, duration, isPaid
                //     );
                // }
                else if (action === "book" || action === "reserve") BookingAction[action]();
            },
            true, "Confirm", "Cancel");
    };

    // ---------------------- BOOKING CONFIRMATION ----------------------
    const confirmBooking = async () => {

        setLoading(true); // show spinner immediately

        try {

            // Initialize cache and user
            const cache = await initAllStores();

            const roomIds: string[] = selectedRooms.map(r => r.id);
            const bookingId = uuidv4();

            const businessDate = getBookingBusinessDate();

            const createdAt = businessDate;
            // const updatedAt = businessDate;

            const refNo = generateReference(
                paymentMethod.toLocaleLowerCase() as PaymentType, "booking"
            );

            // Prepare main booking objects
            const bookingData = {
                guestData: {
                    createdAt,
                    // updatedAt,
                    id: customerId || uuidv4(),
                    name,
                    phone,
                    email,
                    address,
                    occupation,
                    gender: gender || null,
                    nationality,
                    idType: idType || null,
                    idNumber,
                    vechicleNo: vechicleNumber,
                    state,
                },
                billData: { id: uuidv4(), orderedById: user.userId },
                bookingData: {
                    createdAt,
                    // updatedAt,
                    refNo,
                    id: bookingId,
                    description,
                    guestNumber,
                    terminal: terminalId,
                },
            };

            // Fetch all room data in parallel
            const roomsData = await Promise.all(roomIds.map(id => cache.getBy("rooms", { id })));

            // Split discount among rooms
            const sharedDiscount = splitMoney(parseFloat(discount) || 0, roomIds.length);

            // Fetch all booked rooms first (for conflict checking)
            const bookedRooms: BookedRoom[] = await cache.getAllBy("bookedRooms", { isUse: true });

            // Prepare booked room entries
            const bookedRoomData: BookedRoom[] = [];

            for (const [i, room] of roomsData.entries()) {
                const conflictMsg = await checkRoomAvailability(
                    bookedRooms,
                    room.id,
                    checkInDate,
                    checkOutDate
                );

                if (conflictMsg) {
                    setLoading(false);
                    return openModal(
                        "Room Conflict ⚠️",
                        conflictMsg,
                        () => closeModal(),
                        false,
                        "Close"
                    );
                }

                const shortTimeAmount = hotel?.preference?.shortTimeAmount ?? SHORT_TIME_AMOUNT;
                bookedRoomData.push({
                    createdAt,
                    // updatedAt,
                    id: uuidv4(),
                    guestName: name,
                    bookingId,
                    discount: sharedDiscount[i],
                    amount:
                        (bookingType.toLocaleLowerCase() !== "shorttime"
                            ? duration * room.cashPrice
                            : shortTimeAmount)
                        + (earlyCheckIn === "true"
                            ? hotel?.preference?.earlyCheckinAmount
                            ?? EARLY_CHECKIN_AMOUNT : 0),
                    duration,
                    bookingType,
                    checkInDate: attachCurrentTime(checkInDate),
                    checkOutDate,
                    roomId: room.id,
                    paymentMethod: paymentMethod as PaymentType,
                    isUse: true,
                    isPaid: toBool(isPaid as BoolStr),
                    clearedById: toBool(isPaid as BoolStr) ? user.userId : null,
                    terminal: terminalId,
                });
            }

            // Send booking request
            await sendRequest("POST", "/rooms/book", { ...bookingData, bookedRoomData });

            // Update cache in parallel
            const { permissions, userId, ...rest } = user;
            const clearedBy = toBool(isPaid as BoolStr) ? { ...rest, id: userId } : null;
            const mappedBookedRoomData = bookedRoomData.map(b => ({ ...b, ...clearedBy }));

            await Promise.all([
                cache.saveOne("bookings", {
                    ...bookingData.bookingData,
                    billId: bookingData.billData.id,
                    bill: bookingData.billData,
                    customer: bookingData.guestData,
                }),
                cache.updateManyBy("rooms", roomIds, { isOccupied: true }),
                cache.saveMany({ bookedRooms: mappedBookedRoomData }),
            ]);

            // Success modal
            openModal(
                `${action && actions[action]?.title} Successful`,
                `Your ${action && actions[action]?.title.toLowerCase()} has been confirmed successfully.`,
                async () => {
                    if (isPaid === "true") {
                        // Usage
                        const bookingReceiptSummary = getBookingReceiptSummary(
                            bookedRoomData, selectedRooms
                        );

                        const items = bookingReceiptSummary.map(r => ({
                            amount: r.totalAmount.toLocaleString(),
                            desc: `${r.quantity} x ${capitalizeFirst(r.roomName)}`,
                        }));
                        const {
                            vatCharge, serviceCharge,
                        } = await calculateBillCharge(roomTotal, parseFloat(discount));
                        const data = {
                            // receiptNo: string;
                            guest: name, items,
                            total: finalTotal.toLocaleString(),
                            vat: vatCharge.toLocaleString(),
                            paymentMethod, discount: discount,
                            amountInWords: numberToWords(finalTotal),
                            reference: refNo,
                            address: hotel.address,
                            serviceCharge,
                            phone: hotel.phone,
                            email: hotel.email,
                            website: hotel.website,
                            issuedBy: user.firstName + " " + user.lastName,
                        }
                        setReceiptData(data);
                        setShowReceiptModal(true);
                    } else {
                        const billNumber = generatedBillNo("BL",);  // NOTE: Bill number is generated at server.
                        setBill({
                            issuedBy: user.firstName + " " + user.lastName,
                            discount: sharedDiscount.reduce((sum, n) => sum + n, 0),
                            bookings: bookedRoomData ?? [],
                            hotel, guestName: bookingData.guestData.name, billNumber
                        });
                        setShowBill(true);
                    }
                },
                true,
                `View ${isPaid === "true" ? "Receipt" : "Bill"}`,
                "Close"
            );

        } catch (error: any) {

            if (error.status === 409) {
                // Conflict — room already booked
                openModal(
                    "Booking Conflict ⚠️",
                    // err.response.data.message ||
                    "This room has just been booked by another staff. Please refresh to see the latest availability.",
                    async () => {
                        await syncServices(["rooms"]);
                        for (const room of selectedRooms) {
                            handleRemoveRoom(room.id);
                        }
                        setSelectedRooms([]);
                        navigate(-1);
                    },
                    false, "Refresh"
                );
            }

            console.error(error);
        } finally {
            setLoading(false); // hide spinner
        }
    };

    // ---------------------- TOGGLE ACTION ----------------------
    const toggleAction = (bookType: string) => {
        // const nextAction = action === "book" ? "reserve" : "book";

        let id = ""; let nextAction = "";

        if (retrieveId.roomId) id = `?roomId=${retrieveId.roomId}`;
        else if (retrieveId.bookingId) id = `?bookingId=${retrieveId.bookingId}`;

        if (action === "book") {
            nextAction = "reserve";
            setDisabled(false);
        } else if (action === "reserve") {
            nextAction = "book";
            setDisabled(true);  // To disable checkin date
        }

        if (bookType === "shortTime" || bookType === "fullTime") {
            nextAction = "book";
            setDisabled(true);  // To disable checkin date
        } else if (bookType == "reservation") {
            nextAction = "reserve";
            setDisabled(false);
        }

        navigate(`/rooms/${nextAction}${id}`);
    };

    // ---------------------- EFFECT: CALCULATE TOTALS ----------------------
    useEffect(() => {
        const shortTimeAmount = (
            hotel?.preference?.shortTimeAmount
            ?? SHORT_TIME_AMOUNT
        );

        const earlyCheckinAmount = (
            hotel?.preference?.earlyCheckinAmount
            ?? EARLY_CHECKIN_AMOUNT
        );

        const isEarlyCheckin = toBool(earlyCheckIn);

        const roomTotal = bookingType.toLocaleLowerCase() === "shorttime"
            ? selectedRooms.reduce((sum) => sum + shortTimeAmount, 0)
            : selectedRooms.reduce((sum, r) => sum + r.cashPrice, 0) * duration;
        const finalTotal = Math.max(0, roomTotal - (parseFloat(discount) || 0));
        setRoomTotal(roomTotal);

        setFinalTotal(
            finalTotal + (isEarlyCheckin ? earlyCheckinAmount : 0)
        );

        if (bookingType.toLocaleLowerCase() === "shorttime") {
            recalcDuration(checkInDate, today);
        }

    }, [
        discount, checkInDate,
        selectedRooms, duration,
        checkOutDate, bookingType,
        earlyCheckIn
    ]);

    // ---------------------- CONDITIONAL RENDER ----------------------
    if (!action || !actions[action]) return <Navigate to="/404" replace />;


    // ---------------------- ACTION HANDLERS ----------------------
    const BookingAction: Record<BookingAction, any> = {
        book: confirmBooking,
        reserve: confirmBooking,
        // extend: confirmExtendBooking
    };


    const searchGuest = async () => {
        if (!guestSearch.trim()) return;

        setLoading(true);
        try {
            // 🔹 Call your real API
            const response = await sendRequest("GET", `/guests/search?query=${guestSearch}`);

            // ✅ Expecting the backend to return { data: [...] }
            setSearchResults(response || []);
            setShowSearchResults(true);
        } catch (err) {
            console.error(err);
            openModal("Search Failed", "Could not fetch guest data. Try again.", closeModal, false, "Close");
        } finally {
            setLoading(false);
        }
    };

    const fillGuestDetails = (guest: any) => {
        setCustomerId(guest.id);
        setName(guest.name);
        setEmail(guest.email);
        setPhone(guest.phone);
        setAddress(guest.address || "");
        setState(guest.state || "");
        setNationality(guest.nationality || "");
        setGender(guest.gender || "");
        setIdType(guest.idType || "");
        setIdNumber(guest.idNumber || "");
        setOccupation(guest.occupation || "");
        setDescription(guest.description || "");
        setDiscount(guest.discount || 0);
        setGuestNumber(guest.guestNumber || "");
        setVechicleNumber(guest.vechicleNumber || "");
        setShowSearchResults(false); // hide dropdown after selecting
    };

    return (
        <div className="bg-secondary flex-1 p-4 md:p-6 space-y-8 overflow-y-auto">
            <FullPageSpinner show={loading} />

            {/* Bill Modal */}
            {showBill && (
                <Bill
                    billNumber={bill.billNumber}
                    hotel={bill.hotel}
                    discount={bill.discount}
                    guestName={bill.guestName}
                    issuedBy={bill.issuedBy}
                    bookings={bill.bookings}
                    onClose={() => setShowBill(false)}
                />
            )}

            {showReceiptModal && (
                <ReceiptModal
                    sample={receiptData}
                    onClose={() => setShowReceiptModal(false)}
                />
            )}

            {/* Header with Back Arrow and Switch Button */}
            <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">

                    <button
                        onClick={() => {
                            if (retrieveId.roomId) navigate("/rooms");
                            else if (retrieveId.customerId) navigate("/guests");
                            else if (retrieveId.bookingId) navigate("/bookings");
                            else navigate("/dashboard");
                        }}
                        className="p-2 rounded-full hover:bg-gray-100 transition"
                    >
                        <span className="w-5 h-5 text-gray-700">{FaArrowLeft({})}</span>
                    </button>
                    <div>
                        <h1 className="text-2xl font-bold text-blue-600">
                            Add New {action && actions[action]["title"]}
                        </h1>
                        <p className="text-gray-600">
                            Fill in the details to create Room {action && actions[action]["title"].toLocaleLowerCase()}
                        </p>
                    </div>
                </div>

                {/* <button
                    onClick={toggleAction}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                    Switch to {action === "book" ? "Reservation" : "Booking"}
                </button> */}

                <div className="relative w-full md:w-[420px]">
                    {/* 🔍 Search icon */}
                    <span
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 cursor-pointer"
                        onClick={searchGuest}
                    >
                        {FaSearch({})}
                    </span>

                    {/* 🔎 Input field */}
                    <input
                        type="text"
                        placeholder="Search guest by name"
                        value={guestSearch}
                        onChange={(e) => {
                            setGuestSearch(e.target.value);
                            setShowSearchResults(false);
                        }}
                        className="w-full pl-10 pr-3 py-2 border rounded-lg focus:ring focus:ring-blue-300"
                    />

                    {/* 🧩 Search Results Dropdown */}
                    {showSearchResults && (
                        <div className="absolute z-10 w-full mt-2 border rounded-lg max-h-56 overflow-y-auto bg-white shadow-lg">
                            {searchResults.length > 0 ? (
                                searchResults.map((guest: any) => (
                                    <div
                                        key={guest.id}
                                        className="p-2 hover:bg-blue-50 cursor-pointer transition-colors duration-150"
                                        onClick={() => fillGuestDetails(guest)}
                                    >
                                        <p className="font-semibold text-gray-800">{guest.name}</p>

                                        <div className="flex flex-wrap items-center gap-1 text-sm text-gray-500">
                                            {guest.email ? (
                                                <span>{guest.email}</span>
                                            ) : (
                                                <span className="italic text-gray-400">No email</span>
                                            )}

                                            <span className="mx-1">|</span>

                                            {guest.phone ? (
                                                <span>{guest.phone}</span>
                                            ) : (
                                                <span className="italic text-gray-400">No phone</span>
                                            )}
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className="p-2 text-gray-500 italic">No guests found</div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            <div className="bg-white shadow-md rounded-2xl p-6 space-y-6">

                {/* Title */}
                <h2 className="text-lg font-semibold">Guest / Organisation Details</h2>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <input
                        type="text"
                        placeholder="Full Name / Organisation Name"
                        onChange={(e) => setName(e.target.value)}
                        value={name}
                        className="p-3 border rounded-lg focus:ring focus:ring-blue-300"
                    />
                    <input
                        type="email"
                        placeholder="Email Address (Optional)"
                        value={email}
                        onChange={(e) => setEmail(e.target.value.trim())}
                        className="p-3 border rounded-lg focus:ring focus:ring-blue-300"
                    />
                    <input
                        type="tel"
                        placeholder="Phone Number"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value.trim())}
                        className="p-3 border rounded-lg focus:ring focus:ring-blue-300"
                    />
                    <input
                        type="text"
                        placeholder="Address"
                        value={address}
                        onChange={(e) => setAddress(e.target.value)}
                        className="p-3 border rounded-lg focus:ring focus:ring-blue-300"
                    />

                    <input
                        type="text"
                        value={state}
                        placeholder="State (Optional)"
                        onChange={(e) => setState(e.target.value)}
                        className="p-3 border rounded-lg focus:ring focus:ring-blue-300"
                    />

                    <input
                        type="text"
                        placeholder="Nationality (Optional)"
                        value={nationality}
                        onChange={(e) => setNationality(e.target.value)}
                        className="p-3 border rounded-lg focus:ring focus:ring-blue-300"
                    />
                    <select
                        className="p-3 border rounded-lg focus:ring focus:ring-blue-300"
                        onChange={(e) => setGender(e.target.value.trim())}
                        value={gender}
                    >
                        <option value="">Gender (Optional)</option>
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                    </select>

                    <select
                        className="p-3 border rounded-lg focus:ring focus:ring-blue-300"
                        onChange={(e) => setIdType(e.target.value.trim())}
                        value={idType}
                    >
                        <option value="">ID Type (Optional)</option>
                        <option value="nin">NIN</option>
                        <option value="passport">International Passport</option>
                        <option value="driversLicence">Driver’s License</option>
                        <option value="votersCard">Voter’s Card</option>
                    </select>

                    <input
                        type="text"
                        placeholder="ID Number (Optional)"
                        value={idNumber}
                        onChange={(e) => setIdNumber(e.target.value.trim())}
                        className="p-3 border rounded-lg focus:ring focus:ring-blue-300"
                    />

                    <input
                        type="text"
                        placeholder="Occupation (Optional)"
                        value={occupation}
                        onChange={(e) => setOccupation(e.target.value)}
                        className="p-3 border rounded-lg focus:ring focus:ring-blue-300"
                    />

                    <input
                        type="text"
                        placeholder="Vechicle Numer (Optional)"
                        value={vechicleNumber}
                        onChange={(e) => setVechicleNumber(e.target.value.trim())}
                        className="p-3 border rounded-lg focus:ring focus:ring-blue-300"
                    />
                </div>
            </div>

            {/* Booking Details */}
            <div className="bg-white shadow-md rounded-2xl p-6 space-y-6">
                <h2 className="text-lg font-semibold">Booking Details</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

                    <select
                        value={bookingType}
                        onChange={(e) => {
                            const bookType = e.target.value;
                            setBookingType(bookType);
                            setCheckOutDate("");
                            setDisplayDuration("");
                            if (bookType === "shortTime" || bookType === "fullTime") {
                                if (bookType === "shortTime") {
                                    setCheckOutDate(today);
                                    // navigate("book");
                                    //  toggleAction();
                                } else {
                                    // toggleAction();
                                }
                                setDisabled(true);
                            } else if (bookType === "reservation") {
                                setDisabled(false);
                                // navigate("reservere");
                                // toggleAction();
                            }
                            toggleAction(bookType);
                        }}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    >
                        <option value="shortTime">ShortTime</option>
                        <option value="fullTime">FullTime</option>
                        <option value="reservation">Reservation</option>
                    </select>

                    <select
                        value={earlyCheckIn}
                        onChange={(e) => setEarlyCheckin(e.target.value as BoolStr)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    >
                        <option value="">Is Early CheckIn?</option>
                        <option value="true">Yes</option>
                        <option value="false">No</option>
                    </select>

                    <input
                        type="date"
                        value={checkInDate}
                        disabled={isDisabled && bookingType.toLocaleLowerCase() === "shorttime"}
                        onChange={(e) => updateCheckInDate(e.target.value.trim())}
                        className="p-3 border rounded-lg focus:ring focus:ring-blue-300"
                    />
                    <input
                        value={checkOutDate}
                        onChange={(e) => updateCheckOutDate(e.target.value)}
                        className="p-3 border rounded-lg focus:ring focus:ring-blue-300"
                        type={type}
                        placeholder="Checkout Date"
                        onFocus={() => setType("date")}
                        onBlur={(e) => !e.target.value && setType("text")}
                        disabled={isDisabled && bookingType.toLocaleLowerCase() === "shorttime"}
                    />
                    <input
                        placeholder="Duration (auto-calculated from dates)"
                        className="p-3 border rounded-lg bg-gray-100 text-gray-700 cursor-not-allowed focus:ring-0"
                        value={displayDuration}
                        readOnly
                    />

                    <>
                        <select
                            className="p-3 border rounded-lg focus:ring focus:ring-blue-300"
                            onChange={(e) => setIsPaid(e.target.value.trim())}
                            value={isPaid}
                        >
                            <option value="">Payment Status</option>
                            <option value="true">Paid</option>
                            <option value="false">Pending</option>
                        </select>

                        <select
                            className="p-3 border rounded-lg focus:ring focus:ring-blue-300"
                            onChange={(e) => setPaymentMethod(e.target.value.trim())}
                            value={paymentMethod}
                        >
                            <option value="">Payment Method</option>
                            <option value="pos">POS</option>
                            <option value="transfer">Transfer</option>
                            <option value="cash">Cash</option>
                        </select>
                    </>
                    <input
                        type="text"
                        onChange={(e) => setGuestNumber(e.target.value)}
                        value={guestNumber}

                        placeholder="Number of Guests (optional, e.g., 1 male, 1 female)"
                        className="p-3 border rounded-lg focus:ring focus:ring-blue-300"
                    />
                    {/* 
                    <input
                        type="text"
                        placeholder="Description (Optional)"
                        value={description}
                        className="p-3 border rounded-lg focus:ring focus:ring-blue-300"
                        onChange={(e) => setDescription(e.target.value)}
                    /> */}
                    <input
                        type="number"
                        min="0"
                        placeholder="Discount (Optional)"
                        value={discount}
                        onChange={(e) => setDiscount(e.target.value)}
                        className="p-3 border rounded-lg focus:ring focus:ring-blue-300"
                    />
                </div>

                {/* Total Section */}
                <div className="mt-4 p-4 border rounded-lg bg-gray-50 text-right">
                    {checkInDate && checkOutDate && selectedRooms.length > 0 ? (
                        <>
                            <p className="text-lg font-semibold text-gray-800">
                                Total:{" "}
                                <span className="text-blue-600">
                                    ₦{finalTotal.toLocaleString()}
                                </span>
                            </p>
                            {parseFloat(discount) > 0 && (
                                <small className="block text-gray-500">
                                    (Before discount: ₦{roomTotal.toLocaleString()})
                                </small>
                            )}
                        </>
                    ) : (
                        <p className="text-sm text-gray-500 italic">
                            Select check-in date, check-out date, and at least one room to see the total.
                        </p>
                    )}
                </div>

            </div>

            {/* Room Selection */}
            <div className="bg-white shadow-md rounded-2xl p-6 space-y-6">
                <h2 className="text-lg font-semibold">Room Details</h2>

                {/* Room Selector */}
                <div className="flex space-x-4">
                    <select
                        onChange={(e) => handleAddRoom(e.target.value)}
                        className="p-3 border rounded-lg focus:ring focus:ring-blue-300"
                    >
                        <option value="">Select Room Number</option>
                        {rooms[action].map((room) => (
                            <option key={room.id} value={room.id}>
                                {room.number.toLocaleUpperCase()} - {capitalizeFirst(room.name)}
                            </option>
                        ))}
                    </select>
                </div>

                {/* Selected Rooms */}
                <div className="space-y-3">
                    {selectedRooms.map((room) => (
                        <div
                            key={room.number}
                            className="flex justify-between items-center border p-3 rounded-lg shadow-sm hover:shadow-md transition"
                        >
                            <div>
                                <p className="font-medium text-gray-800">
                                    {capitalizeFirst(room.name)} — {" "}
                                    Room {room.number.toLocaleUpperCase()}
                                </p>
                                <p className="text-sm text-gray-500">
                                    ₦{room.cashPrice.toLocaleString()}
                                </p>
                            </div>
                            <button
                                onClick={() => handleRemoveRoom(room.id)}
                                className="text-red-500 hover:text-red-700 transition"
                            >
                                {FaTrash({})}
                            </button>
                        </div>
                    ))}
                </div>
            </div>

            {/* Submit Button */}
            <div className="flex justify-end">
                <button
                    onClick={() => showConfirmationModal()}
                    className="px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg shadow hover:bg-blue-700 transition"
                >
                    Create {action && actions[action]["title"]}
                </button>
            </div>
        </div>
    );
};